<template>
	<TheStdLayout>

		<template #pageheader>
			<div class="flex items-center gap-4">
			{{  $t('Grade-level goals')  }}

				<button type="button" class="btn-help" @click="showInfo = !showInfo" />
			</div>

		</template>

		<div class="px-4 py-6 mx-auto sm:px-6 md:px-8">

			<FloatingPlusButton :pulsate="!goalCollectionsStore.loading && goalCollectionsStore.items.length === 0 ? true : false" @push="createGoalCollection" />

			<div class="col-span-12 mt-16">

				<!-- empty state -->
				<EmptyState v-if="!goalCollectionsStore.loading && goalCollectionsStore.items.length === 0"
					:title="$t('No grade-level goal collections yet')"
					description=""
				/>

				<table v-else class="divide-y divide-base-200 w-full table-fixed">
					<thead>
						<tr>
							<th scope="col" class="table-col-title">
								{{ $t('Name') }}
							</th>
							<th scope="col" class="table-col-title">
								{{ $tc('Goal', 2) }}
							</th>
							<th scope="col" class="table-col-title">
								<div class="flex justify-end">
									<label class="flex items-center gap-2 cursor-pointer normal-case">
										<input type="checkbox" v-model="filter.archived" @change="applyFilter" class="checkbox checkbox-xs" />
										{{ $t('Show archived') }}
									</label>
								</div>
								<span class="sr-only">{{$t('Actions')}}</span>
							</th>
						</tr>
					</thead>

					<tbody v-if="goalCollectionsStore.loading">
						<tr>
							<td colspan="3" class="text-center py-6">
								<span class="loading loading-spinner loading-sm"></span>
							</td>
						</tr>

					</tbody>

					<tbody v-else class="bg-base divide-y divide-base-200">

						<tr class="border-b" v-for="goalCollection in goalCollectionsStore.items">
							<td class="px-4 py-3 print:py-1 whitespace-nowrap" :class="[goalCollection.archivedAt ? 'text-base-content-light' : '']">
								{{ goalCollection.name }}
								<div class="inline" v-if="goalCollection.curriculum && goalCollection.curriculum.name !== organisationsStore.currentOrganisation?.curriculum?.name">
									({{ goalCollection.curriculum.name }})
								</div>
								<div class="badge" v-if="goalCollection.archivedAt">
									{{ $t('Archived') }}
								</div>
								<div class="text-sm text-base-content-light pl-4 max-w-full text-wrap" v-if="goalCollection.classroomIds.length > 0">

									{{ $t('used in: {classrooms}', { classrooms: goalCollection.classrooms.map(c => c.name).join(', ') }) }}
								</div>
							</td>
							<td class="px-4 py-3 print:py-1">
								<div class="flex items-center gap-2">

									<div class="flex items-center">

										<div class="w-12 text-right text-lg font-semibold px-1" :class="[goalCollection.goalCount < 1 ? 'text-danger' : '']">{{ goalCollection.goalCount }}</div>

										<div class="border-l border-gray-300 pl-4 ml-2">

											<router-link v-if="goalCollection.goalCount >= 1" :to="{name: 'goal-collections.collection', params: { collectionId: goalCollection.id }}" class="btn btn-ghost btn-circle btn-sm bg-base-200">
												<MagnifyingGlassIcon class="w-5 h-5" />
											</router-link>

											<router-link v-else-if="!goalCollection.archivedAt" :to="{name: 'goal-collections.collection', params: { collectionId: goalCollection.id }, state: { create: '1' }}" class="btn btn-primary btn-circle btn-sm">
												<PlusIcon class="w-5 h-5"></PlusIcon>
											</router-link>
										</div>
									</div>
								</div>
							</td>
							<td class="px-4 py-3 print:py-1 whitespace-nowrap text-right text-sm font-medium flex justify-end gap-2 sm:gap-4">
								<div class="flex-row print:hidden">

									<template v-if="goalCollection.archivedAt">

										<button class="btn btn-xs btn-ghost bg-base-100" @click="restoreGoalCollection(goalCollection)">
											{{ $t('Restore') }}
										</button>

									</template>

									<template v-else>

										<Dropdown>
											<template #trigger>
												<button class="btn btn-ghost btn-circle bg-base-100">
													<EllipsisVerticalIcon class="w-5 h-5"></EllipsisVerticalIcon>
												</button>
											</template>
											<template #content>
												<DropdownMenu class="w-40">
													<DropdownMenuItem>
														<a @click="editGoalCollection(goalCollection)">
															<PencilIcon class="w-5 h-5" />
															{{ $t('Edit') }}
														</a>
													</DropdownMenuItem>
													<DropdownMenuItem>
														<a @click="duplicateGoalCollection(goalCollection)">
															<DocumentDuplicateIcon class="w-5 h-5" />
															{{ $t('Duplicate') }}
														</a>
													</DropdownMenuItem>
												</DropdownMenu>
											</template>
										</Dropdown>

									</template>
								</div>
							</td>
						</tr>

					</tbody>
				</table>
			</div>
		</div>

	<GoalCollectionEditor
		:goalCollection="selectedGoalCollection"
		:action="selectedGoalAction"
		v-if="selectedGoalCollection"
		@requestClose="closeGoalCollectionEditor"
	></GoalCollectionEditor>

		<KBView v-if="showInfo" :url="{
			en: 'https://handsomely-cicada-2cb.notion.site/ebd/26c0964c11428062b619d633a48a0134',
			nl: 'https://handsomely-cicada-2cb.notion.site/ebd/26c0964c1142803d8742c64fc362c907',
		}" @requestClose="showInfo = false" />

	</TheStdLayout>
</template>

<script lang="ts">
import FloatingPlusButton from '@/components/ui/FloatingPlusButton.vue';
import { PencilIcon, MapPinIcon, MagnifyingGlassIcon, PlusIcon, DocumentDuplicateIcon, EllipsisVerticalIcon } from '@heroicons/vue/24/outline';

import { mapStores } from 'pinia';
import {useGoalCollectionsStore} from "@/stores/GoalCollections.store.ts";
import {GoalCollection} from "@/models/GoalCollection.model.ts";
import GoalCollectionEditor from "@/components/GoalCollectionEditor.vue";
import {useClassroomsStore} from "@/stores/Classrooms.store.ts";
import {useOrganisationsStore} from "@/stores/Organisations.store.ts";
import Dropdown from "@/components/ui/Dropdown.vue";
import DropdownMenu from "@/components/ui/DropdownMenu.vue";
import DropdownMenuItem from "@/components/ui/DropdownMenuItem.vue";
import {GoalCollectionFilterDef} from "@/stores/filters/GoalCollectionFilterDef.ts";

export default {
	components: {
		GoalCollectionEditor,
		FloatingPlusButton,
		PencilIcon, MapPinIcon, PlusIcon, MagnifyingGlassIcon, DocumentDuplicateIcon, EllipsisVerticalIcon,
		Dropdown, DropdownMenu, DropdownMenuItem,
	},

	data() {
		return {
			selectedGoalCollection: null as GoalCollection | null,
			selectedGoalAction: undefined as string | undefined,
			showInfo: false as boolean,
			filter: GoalCollectionFilterDef
		};
	},

	computed: {
		...mapStores(
			useGoalCollectionsStore,
			useClassroomsStore,
			useOrganisationsStore,
		),
	},

	async mounted() {

		Promise.all([
			this.goalCollectionsStore.load(),
			this.classroomsStore.load()
		]);

	},

	created() {
		this.filter = this.goalCollectionsStore.getCurrentFilter();
	},

	methods: {

		applyFilter() {
			this.goalCollectionsStore.applyFilter(this.filter);
		},

		createGoalCollection() {
			this.selectedGoalAction = 'create';
			this.selectedGoalCollection = new GoalCollection();
			// One collection = one curriculum (client-sent at creation, immutable server-side):
			// Collections are school-scoped, so new collections get the school's curriculum.
			this.selectedGoalCollection.curriculumId =
				this.organisationsStore.currentOrganisation?.curriculum?.id ?? null;
		},

		editGoalCollection(goalCollection: GoalCollection) {
			this.selectedGoalAction = 'edit';
			this.selectedGoalCollection = goalCollection;
		},

		duplicateGoalCollection(goalCollection: GoalCollection) {

			const newName = goalCollection.name + ' (' + this.$t('copy') + ')';

			goalCollection = goalCollection.clone();
			goalCollection.name = newName;
			goalCollection.classroomIds = [];

			this.selectedGoalAction = 'duplicate';
			this.selectedGoalCollection = goalCollection;
		},

		restoreGoalCollection(goalCollection: GoalCollection) {
			this.goalCollectionsStore.restore(goalCollection);
		},

		closeGoalCollectionEditor() {
			this.selectedGoalCollection = null;
		}

	}
}
</script>
